import { useNavigate } from "react-router-dom";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";

interface Session {
  session: {
    _id: Id<"sessions">;
    shareCode: string;
    createdAt: number;
    completedAt?: number;
  };
  puzzle: {
    title: string;
    author?: string;
    date: number;
  } | null;
  gameState: {
    grid: any[][];
    startedAt?: number;
  } | null;
}

interface SessionListProps {
  sessions: Session[];
}

export function SessionList({ sessions }: SessionListProps) {
  const navigate = useNavigate();
  const deleteSession = useMutation(api.sessions.deleteSession);

  const handleResume = (shareCode: string) => {
    navigate(`/play/${shareCode}`);
  };

  const handleDelete = async (sessionId: Id<"sessions">) => {
    if (
      confirm("Are you sure you want to delete this session? This cannot be undone.")
    ) {
      try {
        await deleteSession({ sessionId });
      } catch (error) {
        console.error("Failed to delete session:", error);
        alert("Failed to delete session. Please try again.");
      }
    }
  };

  const calculateProgress = (session: Session): number => {
    if (!session.gameState || !session.gameState.grid) return 0;

    const grid = session.gameState.grid;
    let totalCells = 0;
    let filledCells = 0;

    for (const col of grid) {
      for (const cell of col) {
        if (cell.value !== "" || cell.value) {
          totalCells++;
          if (cell.value) filledCells++;
        }
      }
    }

    return totalCells > 0 ? Math.round((filledCells / totalCells) * 100) : 0;
  };

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg">No sessions yet</p>
        <p className="text-sm mt-2">Create your first puzzle to get started!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sessions.map(({ session, puzzle }) => {
        const progress = calculateProgress({ session, puzzle, gameState: null });

        return (
          <div
            key={session._id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900">
                  {puzzle?.title || "Untitled Puzzle"}
                </h3>
                {puzzle?.author && (
                  <p className="text-sm text-gray-600">by {puzzle.author}</p>
                )}
                <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    Started: {new Date(session.createdAt).toLocaleDateString()}
                  </span>
                  {session.completedAt && (
                    <span className="text-green-600 font-medium">
                      ✓ Completed
                    </span>
                  )}
                  {!session.completedAt && progress > 0 && (
                    <span>{progress}% complete</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleResume(session.shareCode)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Resume
                </button>
                <button
                  onClick={() => handleDelete(session._id)}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
